import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { endOfDay, startOfDay } from 'date-fns';
import { toast } from 'sonner';
import type {
  Address,
  AvailabilityResponse,
  Bicycle,
  BookingResult,
  CompanySettings,
  CreateBookingInput,
  CreateWaitingListEntryInput,
  GeocodeSuggestion,
  Intervention,
  Package,
  Product,
  ServiceType,
  Zone,
  ZoneResolution,
} from '@hch/shared';
import { api, ApiError } from './api';

/**
 * Acces aux donnees via TanStack Query.
 *
 * Les cles suivent une hierarchie stable (`['interventions', 'list', filtres]`), ce
 * qui permet d'invalider un sous-arbre entier apres une mutation sans enumerer
 * chaque requete concernee.
 */

export const queryKeys = {
  company: ['company'] as const,
  serviceTypes: ['service-types'] as const,
  packages: (filters?: Record<string, unknown>) => ['packages', filters ?? {}] as const,
  products: (category?: string) => ['products', category ?? 'all'] as const,
  zones: ['zones'] as const,
  zone: (id: string) => ['zones', id] as const,
  availability: (params: Record<string, unknown>) => ['availability', params] as const,
  interventions: (filters?: Record<string, unknown>) =>
    ['interventions', 'list', filters ?? {}] as const,
  intervention: (id: string) => ['interventions', 'detail', id] as const,
  agenda: ['interventions', 'agenda'] as const,
  /** Tournees d'une journee : prefixe `interventions` pour etre invalide avec le reste. */
  tournees: (date: string) => ['interventions', 'tournees', date] as const,
  addresses: ['me', 'addresses'] as const,
  bicycles: ['me', 'bicycles'] as const,
  users: (filters?: Record<string, unknown>) => ['users', filters ?? {}] as const,
  user: (id: string) => ['users', id] as const,
  technicians: ['technicians'] as const,
  templates: ['schedule-templates'] as const,
  slots: (filters: Record<string, unknown>) => ['slots', filters] as const,
  dashboard: ['admin', 'dashboard'] as const,
  audit: (filters: Record<string, unknown>) => ['admin', 'audit', filters] as const,
  waitingList: ['waiting-list'] as const,
};

// ═══════════════════════════════════════════════════════════════════════════════
// Referentiel — donnees stables, cache long
// ═══════════════════════════════════════════════════════════════════════════════

export function useCompany() {
  return useQuery({
    queryKey: queryKeys.company,
    queryFn: () => api.get<CompanySettings>('/company'),
    staleTime: 30 * 60 * 1000,
  });
}

export function useServiceTypes() {
  return useQuery({
    queryKey: queryKeys.serviceTypes,
    queryFn: () => api.get<ServiceType[]>('/service-types'),
    staleTime: 10 * 60 * 1000,
  });
}

export function usePackages(filters: { serviceTypeId?: string; cycleType?: string } = {}) {
  return useQuery({
    queryKey: queryKeys.packages(filters),
    queryFn: () => api.get<Package[]>('/packages', filters),
    staleTime: 10 * 60 * 1000,
  });
}

export function useProducts(category?: string) {
  return useQuery({
    queryKey: queryKeys.products(category),
    queryFn: () => api.get<Product[]>('/products', { category }),
    staleTime: 10 * 60 * 1000,
  });
}

export function useZones() {
  return useQuery({ queryKey: queryKeys.zones, queryFn: () => api.get<Zone[]>('/zones') });
}

// ═══════════════════════════════════════════════════════════════════════════════
// Tunnel de reservation
// ═══════════════════════════════════════════════════════════════════════════════

export function useGeocodeSearch(query: string, enabled = true) {
  return useQuery({
    queryKey: ['geocoding', query],
    queryFn: () => api.get<GeocodeSuggestion[]>('/geocoding/search', { q: query, limit: 5 }),
    // La BAN n'est interrogee qu'a partir de 4 caracteres : en deca, les resultats
    // ne sont pas exploitables et l'appel est gaspille.
    enabled: enabled && query.trim().length >= 4,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
}

export function useZoneResolution(coords: { lat: number; lng: number } | null) {
  return useQuery({
    queryKey: ['zones', 'resolve', coords?.lat, coords?.lng],
    queryFn: () =>
      api.get<ZoneResolution>('/zones/resolve', { lat: coords!.lat, lng: coords!.lng }),
    enabled: !!coords,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAvailability(
  params: { lat: number; lng: number; packageId: string; from: string; to: string } | null,
) {
  return useQuery({
    queryKey: queryKeys.availability(params ?? {}),
    queryFn: () => api.get<AvailabilityResponse>('/availability', params!),
    enabled: !!params,
    // Les disponibilites bougent en permanence : un cache long afficherait des
    // creneaux deja pris et multiplierait les collisions a la validation.
    staleTime: 30 * 1000,
  });
}

/** RG-05 : enregistrement d'un prospect dont l'adresse n'est pas encore desservie. */
export function useJoinWaitingList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateWaitingListEntryInput) =>
      api.post<{ id: string; status: string }>('/zones/waiting-list', input),
    onSuccess: () => {
      // L'ecran « Liste d'attente » du back-office doit refleter l'ajout.
      void queryClient.invalidateQueries({ queryKey: queryKeys.waitingList });
      toast.success('Vos coordonnees ont bien ete enregistrees.');
    },
    onError: (error) => toast.error(messageOf(error)),
  });
}

export function useCreateBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateBookingInput) => api.post<BookingResult>('/bookings', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['availability'] });
      void queryClient.invalidateQueries({ queryKey: ['interventions'] });
    },
    onError: (error) => toast.error(messageOf(error)),
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// Interventions
// ═══════════════════════════════════════════════════════════════════════════════

export function useInterventions(filters: Record<string, string | undefined> = {}) {
  return useQuery({
    queryKey: queryKeys.interventions(filters),
    queryFn: () => api.get<Intervention[]>('/interventions', filters),
  });
}

export function useAgenda() {
  return useQuery({
    queryKey: queryKeys.agenda,
    queryFn: () =>
      api.get<{ today: Intervention[]; upcoming: Intervention[]; past: Intervention[] }>(
        '/interventions/agenda',
      ),
  });
}

/**
 * Interventions d'une journee complete, tous techniciens — carte des tournees.
 *
 * Les bornes sont calculees a partir du jour CIVIL local puis converties en instants
 * UTC : envoyer « 2026-08-17T00:00:00Z » decalerait la journee de deux heures en
 * heure d'ete, et ferait disparaitre les interventions de debut de matinee.
 */
export function useTournees(jour: Date) {
  const from = startOfDay(jour).toISOString();
  const to = endOfDay(jour).toISOString();

  return useQuery({
    queryKey: queryKeys.tournees(from),
    queryFn: () => api.get<Intervention[]>('/interventions', { scope: 'all', from, to }),
  });
}

export function useIntervention(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.intervention(id ?? ''),
    queryFn: () =>
      api.get<Intervention & { availableTransitions: string[] }>(`/interventions/${id}`),
    enabled: !!id,
  });
}

/** Fabrique de mutations sur une intervention : invalide le detail et les listes. */
function useInterventionMutation<TInput>(
  buildRequest: (id: string, input: TInput) => Promise<unknown>,
  successMessage: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: TInput }) => buildRequest(id, input),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.intervention(variables.id) });
      void queryClient.invalidateQueries({ queryKey: ['interventions'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
      toast.success(successMessage);
    },
    onError: (error) => toast.error(messageOf(error)),
  });
}

export const useChangeInterventionStatus = () =>
  useInterventionMutation<{ status: string; reason?: string }>(
    (id, input) => api.post(`/interventions/${id}/status`, input),
    'Statut mis a jour.',
  );

export const useCancelIntervention = () =>
  useInterventionMutation<{ reason: string }>(
    (id, input) => api.post(`/interventions/${id}/cancel`, input),
    'Intervention annulee. Le creneau a ete libere.',
  );

export const useUpdateIntervention = () =>
  useInterventionMutation<Record<string, unknown>>(
    (id, input) => api.patch(`/interventions/${id}`, input),
    'Dossier mis a jour.',
  );

export const useAddComment = () =>
  useInterventionMutation<{ content: string }>(
    (id, input) => api.post(`/interventions/${id}/comments`, input),
    'Commentaire ajoute.',
  );

export const useRegisterPayment = () =>
  useInterventionMutation<{ method: string; amount: number }>(
    (id, input) => api.post(`/interventions/${id}/payment`, input),
    'Encaissement enregistre. Le dossier est clos.',
  );

export const useAttachMedia = () =>
  useInterventionMutation<{ storageKey: string; phase: string }>(
    (id, input) => api.post(`/interventions/${id}/media`, input),
    'Photo ajoutee.',
  );

// ═══════════════════════════════════════════════════════════════════════════════
// Espace personnel
// ═══════════════════════════════════════════════════════════════════════════════

export function useAddresses(enabled = true) {
  return useQuery({
    queryKey: queryKeys.addresses,
    queryFn: () => api.get<Address[]>('/me/addresses'),
    enabled,
  });
}

export function useBicycles(enabled = true) {
  return useQuery({
    queryKey: queryKeys.bicycles,
    queryFn: () => api.get<Bicycle[]>('/me/bicycles'),
    enabled,
  });
}

/** Fabrique generique de mutations CRUD, avec invalidation et retour utilisateur. */
export function useCrudMutation<TInput, TResult = unknown>({
  request,
  invalidate,
  successMessage,
}: {
  request: (input: TInput) => Promise<TResult>;
  invalidate: ReadonlyArray<readonly unknown[]>;
  successMessage: string;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: request,
    onSuccess: () => {
      for (const key of invalidate) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      toast.success(successMessage);
    },
    onError: (error) => toast.error(messageOf(error)),
  });
}

// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Message affichable a l'utilisateur.
 *
 * Un refus metier (409/422) porte deja un texte redige pour l'utilisateur final :
 * il est affiche tel quel. Toute autre erreur est remplacee par un message neutre,
 * afin de ne pas exposer de detail technique.
 */
export function messageOf(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isBusinessRule || error.status === 400 || error.status === 404) {
      return error.message;
    }
    if (error.status === 401) return 'Votre session a expire. Merci de vous reconnecter.';
    if (error.status === 403) return "Vous n'avez pas les droits necessaires pour cette action.";
    if (error.status === 429) return 'Trop de tentatives. Patientez quelques instants.';
  }
  return 'Une erreur est survenue. Merci de reessayer.';
}
